import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { DahliaMark } from '@/components/BrandMark';
import { toast } from 'sonner';
import { ShoppingBag, Plus, Minus, X, ReceiptText, Trash2, ArrowRight } from 'lucide-react';

const fmt = (n, cur = 'NZD') => `$${Number(n || 0).toFixed(2)} ${cur}`;

export default function FounderStore() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({}); // { slug: qty }
  const [placing, setPlacing] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // order returned after checkout
  const [notes, setNotes] = useState('');
  const [address, setAddress] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/store/items');
        setItems(data.items || []);
      } catch (e) {
        toast.error('Could not load the store. Please refresh.');
      } finally { setLoading(false); }
    })();
  }, []);

  const bySlug = useMemo(() => Object.fromEntries(items.map((i) => [i.slug, i])), [items]);

  const cartLines = useMemo(() => Object.entries(cart)
    .filter(([, q]) => q > 0)
    .map(([slug, qty]) => {
      const it = bySlug[slug];
      if (!it) return null;
      const unit = Number(it.sale_price || it.price || 0);
      return {
        slug, qty, unit_price: unit, name: it.name, subtitle: it.subtitle,
        colourway_swatch: it.colourway_swatch, image_url: it.image_url,
        line_total: +(unit * qty).toFixed(2),
      };
    }).filter(Boolean), [cart, bySlug]);

  const subtotal = cartLines.reduce((s, l) => s + l.line_total, 0);

  const addOne = (slug) => setCart((c) => ({ ...c, [slug]: Math.min(20, (c[slug] || 0) + 1) }));
  const removeOne = (slug) => setCart((c) => ({ ...c, [slug]: Math.max(0, (c[slug] || 0) - 1) }));
  const removeAll = (slug) => setCart((c) => { const nc = { ...c }; delete nc[slug]; return nc; });
  const clearCart = () => setCart({});

  const placeOrder = async () => {
    if (cartLines.length === 0) { toast.info('Your cart is empty.'); return; }
    setPlacing(true);
    try {
      const payload = {
        items: cartLines.map((l) => ({ slug: l.slug, qty: l.qty })),
        delivery_address: address,
        delivery_notes: notes,
      };
      const { data } = await api.post('/founders/orders', payload);
      setConfirmed(data.order);
      setCart({});
      toast.success(`Order ${data.order.invoice_number} placed. Bank transfer details below.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to place order.');
    } finally { setPlacing(false); }
  };

  if (loading) return <div className="h-eyebrow" data-testid="store-loading">Loading store...</div>;

  if (confirmed) return <OrderConfirmation order={confirmed} onDone={() => setConfirmed(null)} />;

  return (
    <div className="space-y-6" data-testid="founder-store">
      <div className="h-panel-thin p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="h-eyebrow flex items-center gap-2"><ShoppingBag size={12} /> Founder Store</div>
            <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Add to your order</div>
            <p className="text-sm mt-1 opacity-85 max-w-2xl" style={{ color: 'var(--heritage-plum)' }}>
              Everything in the Heritage Original Collection. Founders pricing. Add to your bundle, pay by bank transfer, and we&rsquo;ll ship with your existing order.
            </p>
          </div>
          <Link to="/console/founder/orders" className="h-btn-outline text-xs" data-testid="store-my-orders-link">
            <ReceiptText size={14} className="mr-2" /> My Orders
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,340px] gap-6">
        {/* Product grid */}
        <div className="grid sm:grid-cols-2 gap-5" data-testid="store-grid">
          {items.map((it) => {
            const inCart = cart[it.slug] || 0;
            return (
              <Panel key={it.slug} className="p-4 flex flex-col" data-testid={`store-card-${it.slug}`}>
                <div className="aspect-[4/5] rounded-lg overflow-hidden bg-[rgba(75,24,56,0.05)] flex items-center justify-center">
                  {it.image_url ? (
                    <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <DahliaMark size={40} />
                  )}
                </div>
                <div className="mt-3 flex-1">
                  <div className="h-eyebrow text-[10px]">{it.colourway_swatch || it.collection_name}</div>
                  <div className="h-serif text-lg mt-0.5 leading-tight" style={{ color: 'var(--heritage-plum)' }}>{it.name}</div>
                  <div className="text-xs opacity-75 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{it.short_description || it.subtitle}</div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="h-serif text-lg" style={{ color: 'var(--heritage-plum)' }}>{fmt(it.sale_price || it.price, it.currency)}</div>
                  {inCart === 0 ? (
                    <button onClick={() => addOne(it.slug)} className="h-btn text-xs" data-testid={`store-add-${it.slug}`}>
                      <Plus size={12} className="mr-1" /> Add
                    </button>
                  ) : (
                    <div className="inline-flex items-center rounded-full" style={{ border: '1.5px solid var(--heritage-plum)' }}>
                      <button onClick={() => removeOne(it.slug)} className="px-2 py-1" data-testid={`store-minus-${it.slug}`} aria-label="decrease"><Minus size={12} style={{ color: 'var(--heritage-plum)' }} /></button>
                      <span className="px-2 h-serif text-sm" style={{ color: 'var(--heritage-plum)' }} data-testid={`store-qty-${it.slug}`}>{inCart}</span>
                      <button onClick={() => addOne(it.slug)} className="px-2 py-1" data-testid={`store-plus-${it.slug}`} aria-label="increase"><Plus size={12} style={{ color: 'var(--heritage-plum)' }} /></button>
                    </div>
                  )}
                </div>
              </Panel>
            );
          })}
        </div>

        {/* Cart panel */}
        <div className="lg:sticky lg:top-4 self-start">
          <Panel className="p-5" data-testid="store-cart">
            <div className="flex items-center justify-between">
              <div className="h-eyebrow">Your cart</div>
              {cartLines.length > 0 && (
                <button onClick={clearCart} className="text-xs opacity-70 hover:opacity-100 flex items-center gap-1" style={{ color: 'var(--heritage-plum)' }} data-testid="cart-clear">
                  <Trash2 size={12} /> Clear
                </button>
              )}
            </div>
            <div className="mt-3 space-y-3 max-h-[360px] overflow-y-auto pr-1">
              {cartLines.length === 0 && (
                <div className="text-sm opacity-70" style={{ color: 'var(--heritage-plum)' }}>Nothing in your cart yet. Add items from the catalogue.</div>
              )}
              {cartLines.map((l) => (
                <div key={l.slug} className="flex items-start gap-3" data-testid={`cart-line-${l.slug}`}>
                  <div className="w-14 h-14 rounded-md overflow-hidden bg-[rgba(75,24,56,0.05)] flex-shrink-0">
                    {l.image_url ? <img src={l.image_url} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="h-serif text-sm leading-tight" style={{ color: 'var(--heritage-plum)' }}>{l.name}</div>
                    <div className="text-[11px] opacity-70" style={{ color: 'var(--heritage-plum)' }}>{l.colourway_swatch}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <button onClick={() => removeOne(l.slug)} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ border: '1px solid var(--heritage-plum)', color: 'var(--heritage-plum)' }} aria-label="decrease"><Minus size={10} /></button>
                      <span className="h-serif text-sm" style={{ color: 'var(--heritage-plum)' }}>{l.qty}</span>
                      <button onClick={() => addOne(l.slug)} className="w-6 h-6 rounded-full flex items-center justify-center" style={{ border: '1px solid var(--heritage-plum)', color: 'var(--heritage-plum)' }} aria-label="increase"><Plus size={10} /></button>
                      <button onClick={() => removeAll(l.slug)} className="ml-auto opacity-70 hover:opacity-100" style={{ color: 'var(--heritage-plum)' }} aria-label="remove"><X size={14} /></button>
                    </div>
                  </div>
                  <div className="h-serif text-sm" style={{ color: 'var(--heritage-plum)' }}>{fmt(l.line_total)}</div>
                </div>
              ))}
            </div>

            {cartLines.length > 0 && (
              <>
                <div className="mt-4 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.2)' }}>
                  <div className="space-y-2">
                    <label className="block text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>Delivery address (optional override)</label>
                    <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} placeholder="Leave blank to use your account address"
                      className="w-full rounded-md px-2 py-1.5 text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }} data-testid="cart-address" />
                    <label className="block text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>Notes (optional)</label>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Any delivery or size notes"
                      className="w-full rounded-md px-2 py-1.5 text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)' }} data-testid="cart-notes" />
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <div className="h-eyebrow">Subtotal</div>
                  <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }} data-testid="cart-subtotal">{fmt(subtotal)}</div>
                </div>
                <div className="text-[11px] opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>Shipping is included with your Founders bundle.</div>
                <button onClick={placeOrder} disabled={placing} className="h-btn w-full mt-4 justify-center" data-testid="cart-checkout">
                  {placing ? 'Placing order...' : (<>Place order <ArrowRight size={14} className="ml-2" /></>)}
                </button>
                <div className="text-[11px] opacity-70 mt-2" style={{ color: 'var(--heritage-plum)' }}>You&rsquo;ll receive an invoice and bank transfer details. Nothing is charged automatically.</div>
              </>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}

function OrderConfirmation({ order, onDone }) {
  return (
    <div className="space-y-5" data-testid="store-order-confirmation">
      <Panel className="p-8">
        <div className="text-center">
          <DahliaMark size={40} />
          <div className="h-eyebrow mt-3">Order received</div>
          <div className="h-display text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Invoice {order.invoice_number}</div>
          <div className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Kia ora {order.user_name || ''} — your order is reserved. Please pay by bank transfer using the details below.</div>
        </div>

        <div className="mt-6 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(75,24,56,0.2)' }}>
          <table className="w-full text-sm">
            <thead style={{ background: 'rgba(75,24,56,0.06)' }}>
              <tr>
                <th className="text-left p-3 h-eyebrow text-[10px]">Item</th>
                <th className="text-center p-3 h-eyebrow text-[10px]">Qty</th>
                <th className="text-right p-3 h-eyebrow text-[10px]">Line total</th>
              </tr>
            </thead>
            <tbody style={{ color: 'var(--heritage-plum)' }}>
              {order.items.map((l) => (
                <tr key={l.slug} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.1)' }}>
                  <td className="p-3">
                    <div className="h-serif">{l.name}</div>
                    <div className="text-[11px] opacity-70">{l.colourway_swatch}</div>
                  </td>
                  <td className="p-3 text-center h-serif">{l.qty}</td>
                  <td className="p-3 text-right h-serif">${l.line_total.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t" style={{ borderColor: 'rgba(75,24,56,0.2)', background: 'rgba(75,24,56,0.04)' }}>
                <td colSpan={2} className="p-3 h-eyebrow text-right">Total</td>
                <td className="p-3 text-right h-serif">${Number(order.total).toFixed(2)} {order.currency}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="rounded-xl p-4" style={{ background: 'rgba(75,24,56,0.05)', border: '1px solid rgba(75,24,56,0.15)' }}>
            <div className="h-eyebrow">Bank transfer</div>
            <div className="mt-2 text-sm" style={{ color: 'var(--heritage-plum)' }}>
              <div><span className="opacity-70">Account name:</span> <span className="h-serif">{order.bank_name}</span></div>
              <div><span className="opacity-70">Account number:</span> <span className="h-serif">{order.bank_account}</span></div>
              <div><span className="opacity-70">Reference:</span> <span className="h-serif" data-testid="order-reference">{order.invoice_number}</span></div>
            </div>
          </div>
          <div className="rounded-xl p-4" style={{ background: 'rgba(75,24,56,0.05)', border: '1px solid rgba(75,24,56,0.15)' }}>
            <div className="h-eyebrow">Delivery</div>
            <div className="mt-2 text-sm whitespace-pre-line" style={{ color: 'var(--heritage-plum)' }}>
              {order.delivery_address || 'Ships with your Founder Bundle.'}
            </div>
            {order.delivery_notes && <div className="mt-2 text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>Notes: {order.delivery_notes}</div>}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <button onClick={onDone} className="h-btn-outline" data-testid="order-confirmation-back">Back to store</button>
          <Link to="/console/founder/orders" className="h-btn" data-testid="order-confirmation-view-orders">View My Orders</Link>
        </div>
      </Panel>
    </div>
  );
}
